import { Controller } from "@hotwired/stimulus"
import { Chart, RadarController, BarController, LineController, DoughnutController, CategoryScale, LinearScale, BarElement, LineElement, PointElement, RadialLinearScale, ArcElement, Legend, Tooltip } from 'chart.js';

// Register the required Chart.js components
Chart.register(
  RadarController,
  BarController, 
  LineController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  ArcElement,
  Legend,
  Tooltip
);

export default class extends Controller {
  static targets = ["teamRadar", "distributionHistogram", "gapVisualization", "beforeAfterComparison", "trendChart"]
  
  static values = { 
    teamData: Object,
    distributionData: Object,
    gapsData: Object,
    beforeAfterData: Object,
    trendData: Object,
    balanceScores: Object
  }

  connect() {
    // Initialize all charts that have data when controller connects
    this.initializeCharts();
  }

  initializeCharts() {
    if (this.hasTeamRadarTarget && this.hasTeamDataValue) {
      this.createTeamRadarChart();
    }
    
    if (this.hasDistributionHistogramTarget && this.hasDistributionDataValue) {
      this.createDistributionHistogram();
    }
    
    if (this.hasGapVisualizationTarget && this.hasGapsDataValue) {
      this.createGapVisualization();
    }
    
    if (this.hasBeforeAfterComparisonTarget && this.hasBeforeAfterDataValue) {
      this.createBeforeAfterComparison();
    }
    
    if (this.hasTrendChartTarget && this.hasTrendDataValue) {
      this.createTrendChart();
    }
  }

  createTeamRadarChart() {
    const ctx = this.teamRadarTarget.getContext('2d');
    const teamData = this.teamDataValue;
    
    // DISC colors
    const colors = {
      dominance: 'rgba(239, 68, 68, 0.7)',     // Red
      influence: 'rgba(234, 179, 8, 0.7)',     // Yellow
      steadiness: 'rgba(34, 197, 94, 0.7)',    // Green
      conscientiousness: 'rgba(59, 130, 246, 0.7)'  // Blue
    };
    
    // Create radar chart for team composition
    new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [{
          label: 'Team Average',
          data: [
            teamData.averages.dominance, 
            teamData.averages.influence, 
            teamData.averages.steadiness, 
            teamData.averages.conscientiousness
          ],
          backgroundColor: 'rgba(79, 70, 229, 0.2)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 2,
          pointBackgroundColor: [
            colors.dominance,
            colors.influence,
            colors.steadiness,
            colors.conscientiousness
          ],
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(79, 70, 229, 1)'
        }]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.raw + '%';
              }
            }
          }
        }
      }
    });
  }

  createDistributionHistogram() {
    const ctx = this.distributionHistogramTarget.getContext('2d');
    const distributionData = this.distributionDataValue;
    
    // Create stacked bar chart for DISC distribution (high, medium, low)
    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [
          {
            label: 'High',
            data: [
              distributionData.d.high,
              distributionData.i.high,
              distributionData.s.high,
              distributionData.c.high
            ],
            backgroundColor: [
              'rgba(239, 68, 68, 0.8)',   // Red (D)
              'rgba(234, 179, 8, 0.8)',   // Yellow (I)
              'rgba(34, 197, 94, 0.8)',   // Green (S)
              'rgba(59, 130, 246, 0.8)'   // Blue (C)
            ],
            borderColor: [
              'rgb(239, 68, 68)',
              'rgb(234, 179, 8)',
              'rgb(34, 197, 94)',
              'rgb(59, 130, 246)'
            ],
            borderWidth: 1
          },
          {
            label: 'Medium',
            data: [
              distributionData.d.medium,
              distributionData.i.medium,
              distributionData.s.medium,
              distributionData.c.medium
            ],
            backgroundColor: [
              'rgba(239, 68, 68, 0.5)',   // Medium Red (D)
              'rgba(234, 179, 8, 0.5)',   // Medium Yellow (I)
              'rgba(34, 197, 94, 0.5)',   // Medium Green (S)
              'rgba(59, 130, 246, 0.5)'   // Medium Blue (C)
            ],
            borderColor: [
              'rgb(239, 68, 68)',
              'rgb(234, 179, 8)',
              'rgb(34, 197, 94)',
              'rgb(59, 130, 246)'
            ],
            borderWidth: 1
          },
          {
            label: 'Low',
            data: [
              distributionData.d.low,
              distributionData.i.low,
              distributionData.s.low,
              distributionData.c.low
            ],
            backgroundColor: [
              'rgba(239, 68, 68, 0.2)',   // Light Red (D)
              'rgba(234, 179, 8, 0.2)',   // Light Yellow (I)
              'rgba(34, 197, 94, 0.2)',   // Light Green (S)
              'rgba(59, 130, 246, 0.2)'   // Light Blue (C)
            ],
            borderColor: [
              'rgb(239, 68, 68)',
              'rgb(234, 179, 8)',
              'rgb(34, 197, 94)',
              'rgb(59, 130, 246)'
            ],
            borderWidth: 1
          }
        ]
      },
      options: {
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ' + context.raw + '%';
              }
            }
          }
        }
      }
    });
  }

  createGapVisualization() {
    const ctx = this.gapVisualizationTarget.getContext('2d');
    const gapsData = this.gapsDataValue;
    
    // Prepare data for visualization
    const labels = [];
    const gapValues = [];
    const idealValues = [];
    const colors = [];
    const traitColors = {
      d: 'rgba(239, 68, 68, 0.7)',   // Red
      i: 'rgba(234, 179, 8, 0.7)',   // Yellow
      s: 'rgba(34, 197, 94, 0.7)',   // Green
      c: 'rgba(59, 130, 246, 0.7)'   // Blue
    };
    
    // Transform gaps data for visualization
    for (const [trait, data] of Object.entries(gapsData)) {
      if (data.level === 'low') {
        labels.push(trait.toUpperCase());
        gapValues.push(data.percentage);
        idealValues.push(25); // Minimum ideal representation
        colors.push(traitColors[trait.toLowerCase()]);
      }
    }
    
    // Create bar chart showing gaps vs ideal values
    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Current Level',
            data: gapValues,
            backgroundColor: colors,
            borderColor: colors.map(color => color.replace('0.7', '1')),
            borderWidth: 1
          },
          {
            label: 'Minimum Ideal',
            data: idealValues,
            backgroundColor: 'rgba(156, 163, 175, 0.3)', // Gray
            borderColor: 'rgba(156, 163, 175, 0.7)',
            borderWidth: 1,
            type: 'line'
          }
        ]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: 50,
            title: {
              display: true,
              text: 'Percentage of Team (%)'
            },
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          },
          x: {
            title: {
              display: true,
              text: 'Underrepresented Traits'
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'Team Trait Gaps Analysis'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ' + context.raw + '%';
              },
              afterBody: function(tooltipItems) {
                const dataIndex = tooltipItems[0].dataIndex;
                const trait = labels[dataIndex].toLowerCase();
                if (gapsData[trait] && gapsData[trait].missing_traits) {
                  return [
                    '',
                    'Missing Traits:',
                    ...gapsData[trait].missing_traits.map(t => '• ' + t)
                  ];
                }
                return [];
              }
            }
          }
        }
      }
    });
  }

  createBeforeAfterComparison() {
    const ctx = this.beforeAfterComparisonTarget.getContext('2d');
    const comparisonData = this.beforeAfterDataValue;
    
    // Create radar chart comparing before and after scenarios
    new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [
          {
            label: 'Current Team',
            data: [
              comparisonData.current.dominance,
              comparisonData.current.influence,
              comparisonData.current.steadiness,
              comparisonData.current.conscientiousness
            ],
            backgroundColor: 'rgba(79, 70, 229, 0.2)',
            borderColor: 'rgba(79, 70, 229, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(79, 70, 229, 1)',
            pointBorderColor: '#fff'
          },
          {
            label: 'With New Member',
            data: [
              comparisonData.projected.dominance,
              comparisonData.projected.influence,
              comparisonData.projected.steadiness,
              comparisonData.projected.conscientiousness
            ],
            backgroundColor: 'rgba(245, 158, 11, 0.2)',
            borderColor: 'rgba(245, 158, 11, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(245, 158, 11, 1)',
            pointBorderColor: '#fff'
          }
        ]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ' + context.raw + '%';
              }
            }
          }
        }
      }
    });
    
    // If we have balance scores, add comparison information
    if (this.hasBalanceScoresValue && this.balanceScoresValue.current && this.balanceScoresValue.projected) {
      const balanceScores = this.balanceScoresValue;
      const currentScore = balanceScores.current;
      const projectedScore = balanceScores.projected;
      const change = projectedScore - currentScore;
      
      // Add balance scores comparison to the DOM
      const comparisonElement = document.createElement('div');
      comparisonElement.className = 'text-center mt-4';
      comparisonElement.innerHTML = `
        <div class="text-lg font-semibold">Team Balance Impact</div>
        <div class="grid grid-cols-3 gap-2 mt-2">
          <div class="text-center">
            <div class="text-sm font-medium text-gray-600">Current</div>
            <div class="text-xl font-bold ${this.getColorClass(currentScore)}">${currentScore.toFixed(1)}%</div>
          </div>
          <div class="text-center">
            <div class="text-sm font-medium text-gray-600">Projected</div>
            <div class="text-xl font-bold ${this.getColorClass(projectedScore)}">${projectedScore.toFixed(1)}%</div>
          </div>
          <div class="text-center">
            <div class="text-sm font-medium text-gray-600">Change</div>
            <div class="text-xl font-bold ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600'}">
              ${change > 0 ? '+' : ''}${change.toFixed(1)}%
            </div>
          </div>
        </div>
      `;
      
      this.beforeAfterComparisonTarget.parentNode.appendChild(comparisonElement);
    }
  }

  createTrendChart() {
    const ctx = this.trendChartTarget.getContext('2d');
    const trendData = this.trendDataValue;
    
    // Create line chart for balance score trends
    new Chart(ctx, {
      type: 'line',
      data: {
        labels: trendData.labels,
        datasets: [
          {
            label: 'Balance Score',
            data: trendData.balanceScores,
            backgroundColor: 'rgba(79, 70, 229, 0.2)',
            borderColor: 'rgba(79, 70, 229, 1)',
            borderWidth: 2,
            tension: 0.3,
            fill: true
          },
          {
            label: 'Trait Distribution',
            data: trendData.distributionScores,
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            borderColor: 'rgba(239, 68, 68, 1)',
            borderWidth: 2,
            tension: 0.3,
            hidden: true
          },
          {
            label: 'Coverage',
            data: trendData.coverageScores,
            backgroundColor: 'rgba(234, 179, 8, 0.2)',
            borderColor: 'rgba(234, 179, 8, 1)',
            borderWidth: 2,
            tension: 0.3,
            hidden: true
          },
          {
            label: 'Strength Clarity',
            data: trendData.strengthScores,
            backgroundColor: 'rgba(34, 197, 94, 0.2)',
            borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 2,
            tension: 0.3,
            hidden: true
          }
        ]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: 'Score'
            },
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          },
          x: {
            title: {
              display: true,
              text: 'Assessment Date'
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'Team Balance Trends Over Time'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ' + context.raw + '%';
              }
            }
          }
        }
      }
    });
  }
  
  // Helper to get color class based on score
  getColorClass(score) {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  }
}